import { Component, OnInit, EventEmitter, Input, Output, AfterViewInit, ViewChild } from '@angular/core';
import { MediaObserver, MediaChange } from '@angular/flex-layout';

import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';

import { environment } from '../../../environments/environment';
import { ErrorHandler, HandleError } from '../../services/error-handler.service';
import { QuickPayPaymentService } from '../../services/quickpay-payment.service';
import { CompanyService } from '../../services/company.service';

import { OpenNotesQuickPayPaymentInfoListItem, OpenNotesQuickPayPaymentInfoListGroupFooter } from 'src/app/models/quickpay';
import { CompanyInformation } from '../../models/company';

export enum QuickPayPaymentSelectType {
    Note,
    PayGroup
}

export interface QuickPayPaymentSelected {
    quickpaypaymentSelectType: QuickPayPaymentSelectType;
    selectedId: any;
    selectedData: OpenNotesQuickPayPaymentInfoListItem[];
}

@Component({
    selector: 'app-quick-pay-payment-note-list',
    templateUrl: './quick-pay-payment-note-list.component.html',
    styleUrls: ['./quick-pay-payment-note-list.component.scss']
})
export class QuickPayPaymentNoteListComponent implements OnInit, AfterViewInit {
    private _acct_type = "";
    private _participantId = "";
    private _lastName = "";
    private _zipCode = "";
    private _noteId = 0;
    private _payGroup = 0;

    @Input()
    set acct_type(acct_type: string) {
        this._acct_type = acct_type || null;
    }
    get acct_type(): string {
        return this._acct_type;
    }

    @Input()
    set participantId(participantId: string) {
        this._participantId = participantId || "";
    }
    get participantId(): string {
        return this._participantId;
    }

    @Input()
    set lastName(lastName: string) {
        this._lastName = lastName || "";
    }
    get lastName(): string {
        return this._lastName;
    }

    @Input()
    set zipCode(zipCode: string) {
        this._zipCode = zipCode || "";
        this.getData();
    }
    get zipCode(): string {
        return this._zipCode;
    }

    @Input() displayTitle = false;

    @Input() displayNotices = false;

    @Output() selected = new EventEmitter<QuickPayPaymentSelected>();

    // Have to use the following ElementDisplayStyle variables and ngStyle to show/hide table elements
    // This because the table paginator will not attach correctly otherwise - this is a work around for a known issue
    private _hideElementDisplayStyle = 'none';
    private _showElementDisplayStyle = 'inline';
    private _showElementDisplayStyleIE = 'inline-block';
    private companyCode: string = null;                 // 10/22/2021

    private _handleError: HandleError;
    private _logging = environment.logging;

    isBrowserIE = /msie\s|trident\//i.test(window.navigator.userAgent);

    showSpinner = false;

    openNotesQuickPayPaymentInfoList: (OpenNotesQuickPayPaymentInfoListItem)[] = [];
    openNotesQuickPayPaymentInfoListWithFooters: (OpenNotesQuickPayPaymentInfoListItem | OpenNotesQuickPayPaymentInfoListGroupFooter)[] = [];

    // DataTable
    tableDisplayedColumns = [
        'noteId',
        'nextPaymentDueDate',
        'totalAmountDue',
        'options'
    ];
    tableDataSource = new MatTableDataSource<OpenNotesQuickPayPaymentInfoListItem | OpenNotesQuickPayPaymentInfoListGroupFooter>(
        this.openNotesQuickPayPaymentInfoListWithFooters
    );
    tableDisplayStyle: string = this._hideElementDisplayStyle;
    tableDisplayStyleIE: string = this._hideElementDisplayStyle;
    openNotesQuickPayPaymentInfoListFound = false;

    companyInformation: CompanyInformation;


    filteredNotesList = [...this.openNotesQuickPayPaymentInfoList];
    selectedNoteState = '';
    uniqueNoteStates: string[] = [];

    constructor(
        private _errorHandler: ErrorHandler,
        private _quickpaypaymentService: QuickPayPaymentService,
        private _companyService: CompanyService,
        public media: MediaObserver
    ) {
        this._handleError = _errorHandler.createHandleError('QuickPayPaymentNoteListComponent');
    }

    ngOnInit() {
        this.openNotesQuickPayPaymentInfoListFound = true;
        this.tableDisplayStyle = this.openNotesQuickPayPaymentInfoListFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
        this.tableDisplayStyleIE = this.openNotesQuickPayPaymentInfoListFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;
    }

    ngAfterViewInit() { }

    getData(): void {
        this.showSpinner = true;

        this.companyInformation = this._companyService.getNewCompanyInformation();
        this.companyCode = this._companyService.getCompanyCode();

        this._companyService.companyInformation.subscribe(
            response => (this.companyInformation = response),
            error => this._handleError('companyInformation.subscribe', error)
        );

        this.getOpenNotesQuickPayPaymentInfoList(this.acct_type, this._participantId, this.lastName, this.zipCode);
    }

    getOpenNotesQuickPayPaymentInfoList(acct_type: string, participantId: string, lastName: string, zipCode: string): void {
        this.showSpinner = true;
        this._quickpaypaymentService.getOpenNotesQuickPayPaymentInfoList(acct_type, participantId, lastName, zipCode).subscribe(
            resultArray => {
                this.openNotesQuickPayPaymentInfoList = resultArray;
                this.filteredNotesList = [...this.openNotesQuickPayPaymentInfoList];
                this.uniqueNoteStates = [...new Set(this.openNotesQuickPayPaymentInfoList.map(note => note.noteState))];
                this.openNotesQuickPayPaymentInfoListWithFooters = this._quickpaypaymentService.addOpenNotesQuickPayPaymentInfoListFooters(resultArray);

                if (this._logging.serviceCallTraceLog) {
                    console.log('QuickPay_PaymentNoteListComponent.getOpenNotesQuickPayPaymentInfoList :: result.length = ' + resultArray.length);
                }
                if (this._logging.serviceDataTraceLog) {
                    console.log('QuickPay_PaymentNoteListComponent.getOpenNotesQuickPayPaymentInfoList :: result = ' + JSON.stringify(resultArray));
                }
                this.tableDataSource = new MatTableDataSource<OpenNotesQuickPayPaymentInfoListItem | OpenNotesQuickPayPaymentInfoListGroupFooter>(
                    this.openNotesQuickPayPaymentInfoListWithFooters
                );
                this.openNotesQuickPayPaymentInfoListFound = resultArray.length > 0;
                this.tableDisplayStyle = this.openNotesQuickPayPaymentInfoListFound ? this._showElementDisplayStyle : this._hideElementDisplayStyle;
                this.tableDisplayStyleIE = this.openNotesQuickPayPaymentInfoListFound ? this._showElementDisplayStyleIE : this._hideElementDisplayStyle;
                this.getDataComplete();
            },
            error => this._handleError('getOpenNotesQuickPayPaymentInfoList', error)
        );
    }

    filterNotes() {

        // this.selectedNoteId = null;

        if (this.selectedNoteState) {
            this.filteredNotesList = this.openNotesQuickPayPaymentInfoList.filter(
                note => note.noteState === this.selectedNoteState
            );
        } else {
            this.filteredNotesList = [...this.openNotesQuickPayPaymentInfoList];
        }
    }

    getDataComplete(): void {
        this.showSpinner = false;
    }

    selectNote(noteId: number) {
        let quickpaypaymentSelected: QuickPayPaymentSelected;
        const selectedNoteId: number = noteId !== null && noteId > 0 ? noteId : null;
        quickpaypaymentSelected = {
            quickpaypaymentSelectType: QuickPayPaymentSelectType.Note,
            selectedId: selectedNoteId,
            selectedData: this._getSelectedData(QuickPayPaymentSelectType.Note, selectedNoteId, null, this.openNotesQuickPayPaymentInfoList)
        };
        this.selected.emit(quickpaypaymentSelected);
    }

    selectPayGroup(paymentGroup: string) {
        let quickpaypaymentSelected: QuickPayPaymentSelected;
        const selectedPayGroup: string = paymentGroup !== null && paymentGroup.length > 0 ? paymentGroup : null;
        quickpaypaymentSelected = {
            quickpaypaymentSelectType: QuickPayPaymentSelectType.PayGroup,
            selectedId: selectedPayGroup,
            selectedData: this._getSelectedData(QuickPayPaymentSelectType.PayGroup, null, selectedPayGroup, this.openNotesQuickPayPaymentInfoList)
        };
        this.selected.emit(quickpaypaymentSelected);
    }

    isGroupFooter(index, item): boolean {
        return item.isGroupFooter;
    }

    getToolTip(element: string): string {
        let toolTip: string;
        switch (element.toUpperCase().trim()) {
            case 'NOTESTATUS': {
                toolTip = 'What does My Loan Status Mean?';
                break;
            }
            default: {
                toolTip = 'No ToolTip message has been defined for ' + element;
                break;
            }
        }
        return toolTip;
    }

    openToolTip(element: string): void {
        switch (element.toUpperCase().trim()) {
            case 'NOTESTATUS': {
                if (this._logging.navigationTraceLog) {
                    console.log('QuickPayPaymentNoteListComponent.openToolTip');
                }
                window.open(this.companyInformation.loanStatusDescriptionsURL, '_blank');
                break;
            }
        }
    }

    // 10/22/2021 - Get Company Code
    QuickPay_CompanyCode_Check(): boolean {
        for (const companyLookup of environment.companyCode.legaltext) {
            if (companyLookup.companyCode === this.companyCode) {
                return true;
            }
        }
        return false;
    }

    private _getSelectedData(
        quickpaypaymentSelectType: QuickPayPaymentSelectType,
        selectedNoteId: number,
        selectedPayGroup: string,
        openNotesQuickPayPaymentInfoList: OpenNotesQuickPayPaymentInfoListItem[]
    ): OpenNotesQuickPayPaymentInfoListItem[] {
        let selectedData: OpenNotesQuickPayPaymentInfoListItem[] = [];
        if (quickpaypaymentSelectType === QuickPayPaymentSelectType.Note) {
            selectedData = openNotesQuickPayPaymentInfoList.filter(x => x.noteId === selectedNoteId);
        } else if (quickpaypaymentSelectType === QuickPayPaymentSelectType.PayGroup) {
            selectedData = openNotesQuickPayPaymentInfoList.filter(x => x.paymentGroup === selectedPayGroup);
        }
        return selectedData;
    }
}
